/**
 * Normalize a `builder_page` decoupled response into the internal `page` state
 * shape: `{ pageNid, slug, title, published, isTemplate, siteId, siteName, meta,
 * redirects: [{from, code}],
 * blocs: [{id, nid, type, data, hiddenOn, syncUrls}], modelFor }`.
 *
 * Shared by the page load (`BuilderCanvas`) AND the save (`useBuilderSave`):
 * `/api/builder/page` and `/api/builder/save` return the SAME decoupled shape,
 * so a single normalizer covers both. This is what lets a just-saved block
 * carry its `submission_url` (harvested into `syncUrls`) immediately — it
 * becomes a per-unit DataSync unit with no reload.
 *
 * The frontend deals with `id` = stable builderId (from `field_block_data`)
 * and `nid` = Drupal node id (for save round-trips).
 */
/**
 * Les métadonnées `<head>` d'un contenu du builder, décodées.
 *
 * `field_meta_tags` est un blob JSON exposé BRUT par le display decoupled : la
 * réponse porte donc une chaîne (ou déjà un objet, selon le formatter). Une
 * page et un CONTENU servi par son modèle portent le même champ et le même
 * panneau d'édition — un seul décodage pour les deux.
 */
export const parseMetaBlob = (raw) => {
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (e) {
      console.warn('Builder: failed to parse meta', e)
    }
    return {}
  }
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
}

export const normalizePage = (payload) => {
  // Le contrat decoupled a DEUX formes : avec droit d'écriture les champs sont
  // imbriqués sous `data`, en lecture seule la réponse est plate. On choisit le
  // conteneur qui porte réellement des champs de PAGE plutôt que de parier sur
  // `data` — une clé `data` sans rien d'autre que le statut vidait la page.
  //
  // Le test ne peut PAS porter sur les seuls `blocs` : le display decoupled
  // masque un champ vide (`decoupled_field_hide_empty`), donc une page SANS
  // BLOC n'a pas de clé `blocs` du tout. On retombait alors sur l'enveloppe,
  // où `slug`, `title`, `siteId` et `isTemplate` n'existent pas — une page
  // fraîchement créée s'ouvrait « Aucun site », sans titre, et un modèle sans
  // son badge. On cherche donc n'importe lequel des champs de la page.
  const candidate = payload?.data
  const PAGE_KEYS = ['blocs', 'slug', 'title', 'id']
  const pageData = (candidate && typeof candidate === 'object' && PAGE_KEYS.some(k => k in candidate))
    ? candidate
    : (payload ?? {})
  const rawBlocs = pageData.blocs ?? []
  const blocs = rawBlocs.map(item => {
    // Même règle qu'au niveau de la page : un bloc n'est imbriqué sous `data`
    // que si l'utilisateur a l'écriture ; en lecture seule il arrive à plat.
    // Lire `item.data` sans condition rendait `objectData` introuvable pour un
    // visiteur anonyme — donc `builderId` absent, `crypto.randomUUID()` appelé,
    // et comme l'origine http n'est PAS un contexte sécurisé cette fonction
    // n'existe pas : le normaliseur levait et la page s'affichait vide.
    const bd = (item?.data && typeof item.data === 'object') ? item.data : (item ?? {})
    // C1 invariant: builder_unit's decoupled output carries its data under the
    // single key `objectData` (one bundle, one formatter). Distinct from the
    // front-internal `apiData.data.objectData` seed that BuilderBlockMount builds.
    const inner = bd.objectData ?? {}
    // Each bloc arrives as a full Syncable Object. Harvest ONLY the management
    // URLs here; the `apiData` envelope itself is synthesized in one place
    // (BuilderBlockMount) from the bloc — so saved and freshly-added blocks
    // share a single construction path (no duplicate shaping). `submission_url`
    // presence = Drupal granted write (B0 gates it); null ⇒ read-only.
    const syncUrls = item?.submission_url
      ? {
          item_url: item.item_url ?? null,
          submission_url: item.submission_url,
          delete_url: item.delete_url ?? null,
        }
      : null
    return {
      // Repli sans `crypto.randomUUID` : indisponible hors contexte sécurisé
      // (http), où il lèverait au lieu de simplement dégrader.
      id: inner.builderId ?? `bloc-${bd.id ?? Math.random().toString(36).slice(2, 10)}`,
      nid: bd.id ?? null,
      type: bd.type,
      data: inner.data ?? {},
      hiddenOn: inner.hiddenOn ?? [],
      syncUrls,
    }
  })

  // `modelFor` is stored as a JSON string in field_model_for; the decoupled
  // view display exposes it raw. Parse here so the rest of the app deals
  // with the structured form. Tolerate empty / invalid (legacy pages).
  let modelFor = []
  const rawModelFor = pageData.modelFor
  if (typeof rawModelFor === 'string' && rawModelFor.trim()) {
    try {
      const parsed = JSON.parse(rawModelFor)
      if (Array.isArray(parsed)) modelFor = parsed
    } catch (e) {
      console.warn('Builder: failed to parse modelFor', e)
    }
  } else if (Array.isArray(rawModelFor)) {
    modelFor = rawModelFor
  }

  // `meta` (métadonnées de référencement/partage) est stocké en JSON dans
  // `field_meta_tags` et exposé brut par le display decoupled — même patron que
  // `modelFor`. On le parse ici pour que le reste de l'app manipule un objet.
  const meta = parseMetaBlob(pageData.meta)

  // `redirects` (anciennes adresses menant à cette page) est un champ LIEN
  // multivalué : le formatter `decoupled_link` sort `{url, title, attributes}`,
  // où `url` est le chemin déjà résolu (le `internal:` du stockage est retiré
  // par Drupal) et `attributes` porte les options du lien — dont le code de
  // statut. On aplatit en `{from, code}`, la forme que le save reposte.
  const redirects = (Array.isArray(pageData.redirects) ? pageData.redirects : [])
    .map(item => ({
      from: String(item?.url ?? ''),
      code: Number(item?.attributes?.code) || 301,
    }))
    .filter(r => r.from !== '')

  return {
    pageNid: pageData.id ?? null,
    slug: pageData.slug ?? '',
    title: pageData.title ?? '',
    // Rattachement à un site (groupe `site`) : pas un champ du nœud mais une
    // relation de groupe, injectée dans la sortie decoupled par orbit_site.
    // `null` = page rattachée à aucun site (visible depuis tous).
    siteId: pageData.siteId ?? null,
    siteName: pageData.siteName ?? '',
    meta,
    // Statut de publication du node page, injecté par le proxy sous
    // `data.published` (la clé `status` est réservée par l'enveloppe Syncable).
    // Absent (payload legacy) ⇒ publiée, comme le défaut Drupal à la création.
    published: pageData.published !== false,
    // Page MODÈLE : point de départ éditorial, jamais servi publiquement (cf.
    // `field_is_template`). Absent (payload legacy, page pas encore créée) ⇒
    // page ordinaire, comme le défaut du champ.
    isTemplate: pageData.isTemplate === true || pageData.isTemplate === 1 || pageData.isTemplate === '1',
    // Catégories du site portées par la page (`field_tags`) : des tid, que le
    // display decoupled sort en CHAÎNES (`decoupled_entity_reference_id`).
    // Normalisés en nombres ici, une fois : c'est la forme du back (le save
    // les recaste), celle qu'Elasticsearch indexe, et la seule qui compare
    // juste avec l'id d'une catégorie fraîchement créée.
    // Champ vide ⇒ clé absente du payload (`decoupled_field_hide_empty`).
    tags: Array.isArray(pageData.tags)
      ? pageData.tags.map(Number).filter(Number.isFinite)
      : [],
    // Anciennes adresses qui mènent encore ici. De l'état de PAGE, comme le
    // slug : posé par le panneau « Adresse », persisté par le Save du header.
    redirects,
    blocs,
    modelFor,
  }
}

export default normalizePage
